import React, { useState, useEffect } from "react";
import { IoTrashBin } from "react-icons/io5";
import { LuImagePlus } from "react-icons/lu";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { BeatLoader } from "react-spinners";
import TopBar from "../../components/TopBar";
import BannerUploadModal from "../../components/BannerUploadModal";
import supabase from "../../supabase";

const Banners = () => {
  const [banners, setBanners] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [locations] = useState(["Banner 1", "Banner 2", "Banner 3", "Banner 4", "Banner 5"]);

  useEffect(() => {
    (async () => {
      const { data } = await supabase.from('banners').select('*').order('position');
      setBanners(data || []);
    })();
  }, []);

  const handleAddBanner = async (bannerData) => {
    setLoading(true);
    try {
      // Upload file to Supabase Storage
      const file = bannerData.file;
      const filePath = `banners/${Date.now()}-${file.name}`;
      const { error: uploadErr } = await supabase.storage.from('public-assets').upload(filePath, file, { upsert: true });
      if (uploadErr) throw uploadErr;
      const { data: publicUrl } = supabase.storage.from('public-assets').getPublicUrl(filePath);

      // Upsert banner row by location
      const location = bannerData.location;
      const position = Number(location.replace(/[^0-9]/g, '')) || null;
      const { data: upserted, error: upsertErr } = await supabase
        .from('banners')
        .upsert({ location, position, image_url: publicUrl.publicUrl }, { onConflict: 'location' })
        .select()
        .maybeSingle();
      if (upsertErr) throw upsertErr;

      // Refresh list
      const { data } = await supabase.from('banners').select('*').order('position');
      setBanners(data || []);
      toast.success("Banner saved");
      setIsModalOpen(false);
    } catch (error) {
      toast.error("Failed to add banner");
      console.error("Error adding banner:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteBanner = async (id) => {
    try {
      await supabase.from('banners').delete().eq('id', id);
      setBanners(banners.filter(banner => banner.id !== id));
      toast.success("Banner deleted successfully");
    } catch (error) {
      toast.error("Failed to delete banner");
      console.error("Error deleting banner:", error);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <TopBar />
      <div className="p-6 mt-16">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-bold text-gray-800">Manage Banners</h1>
          <button
            onClick={() => setIsModalOpen(true)}
            className="bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600 transition-colors flex items-center gap-2"
            disabled={loading}
          >
            {loading ? (
              <BeatLoader size={8} color="white" />
            ) : (
              <>
                <LuImagePlus size={20} />
                Add New Banner
              </>
            )}
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {locations.map((location) => {
            const banner = banners.find(b => b.location === location);
            return (
              <div key={location} className="bg-white rounded-lg shadow-md overflow-hidden">
                <div className="aspect-w-16 aspect-h-9">
                  {banner ? (
                    <img
                      src={banner.image_url}
                      alt={location}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center bg-gray-100">
                      <p className="text-gray-400">No image uploaded</p>
                    </div>
                  )}
                </div>
                <div className="p-4">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-700 font-medium">{location}</span>
                    {banner && (
                      <button
                        onClick={() => handleDeleteBanner(banner.id)}
                        className="text-red-500 hover:text-red-700 transition-colors p-2 rounded-full hover:bg-red-50"
                      >
                        <IoTrashBin size={20} />
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <BannerUploadModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          onSubmit={handleAddBanner}
          locations={locations}
        />
      </div>
    </div>
  );
};

export default Banners;
